import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const VOLUNTARY_TYPES = ['Resignation', 'Retirement', 'End of Contract'];
const INVOLUNTARY_TYPES = ['Termination', 'Abandonment', 'Other'];

const VOLUNTARY_CHECKLIST = [
  { key:'notice_received', label:'Resignation/notice letter received' },
  { key:'exit_interview_done', label:'Exit interview completed' },
  { key:'tasks_reassigned', label:'All open tasks reassigned' },
  { key:'customers_reassigned', label:'Customers/records reassigned' },
  { key:'company_phone_retrieved', label:'Company phone/device retrieved' },
  { key:'access_cards_retrieved', label:'Access cards/keys retrieved' },
  { key:'kiosk_pin_disabled', label:'Kiosk PIN disabled' },
  { key:'sessions_revoked', label:'All active sessions revoked' },
  { key:'login_disabled', label:'System login disabled' },
  { key:'payroll_finalised', label:'Final payroll processed' },
  { key:'clearance_signed', label:'Clearance form signed by staff' },
];

const INVOLUNTARY_CHECKLIST = [
  { key:'disciplinary_complete', label:'Disciplinary process completed' },
  { key:'termination_letter_issued', label:'Termination letter issued' },
  { key:'tasks_reassigned', label:'All open tasks reassigned' },
  { key:'customers_reassigned', label:'Customers/records reassigned' },
  { key:'company_phone_retrieved', label:'Company phone/device retrieved' },
  { key:'access_cards_retrieved', label:'Access cards/keys retrieved' },
  { key:'kiosk_pin_disabled', label:'Kiosk PIN disabled' },
  { key:'sessions_revoked', label:'All active sessions revoked' },
  { key:'login_disabled', label:'System login disabled' },
  { key:'payroll_finalised', label:'Final payroll processed' },
  { key:'clearance_signed', label:'Clearance acknowledged by HR' },
];

export default function Offboarding() {
  const { user } = useAuth();
  const [offboardings, setOffboardings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [category, setCategory] = useState('voluntary');
  const [activeTab, setActiveTab] = useState('all');
  const [form, setForm] = useState({
    user_id:'', exit_type:'Resignation', effective_date:'',
    final_working_date:'', tasks_reassigned_to:'', payroll_status:'',
    notes:'', notice_period_days:'', notice_given_date:'',
    disciplinary_ref:'', termination_reason:''
  });

  const load = () => {
    setLoading(true);
    Promise.all([get('/offboarding'), get('/users')])
      .then(([ob, u]) => {
        setOffboardings(ob.data||[]);
        setUsers((u.data||[]).filter(us => us.role !== 'super_admin'));
      }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const initiate = async () => {
    if (!form.user_id || !form.exit_type || !form.effective_date) {
      alert('Employee, exit type and effective date are required'); return;
    }
    if (category === 'involuntary' && !form.termination_reason) {
      alert('Termination reason is required for involuntary exits'); return;
    }
    setSaving(true);
    try {
      await post('/offboarding', { ...form, category });
      setShowForm(false);
      setForm({ user_id:'', exit_type:'Resignation', effective_date:'',
        final_working_date:'', tasks_reassigned_to:'', payroll_status:'',
        notes:'', notice_period_days:'', notice_given_date:'',
        disciplinary_ref:'', termination_reason:'' });
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const updateChecklist = async (id, step, completed) => {
    try {
      await put('/offboarding/'+id+'/checklist', { step, completed });
      load();
    } catch(err) { alert(err.message); }
  };

  const completeOffboarding = async (id, employeeName) => {
    if (!window.confirm('Complete offboarding for '+employeeName+'? This will permanently disable their account.')) return;
    try {
      await put('/offboarding/'+id+'/complete', {});
      load(); setSelected(null);
      alert('Offboarding complete. Account disabled. History preserved.');
    } catch(err) { alert(err.message); }
  };

  const getChecklistProgress = (checklist, cat) => {
    if (!checklist) return 0;
    const cl = typeof checklist === 'string' ? JSON.parse(checklist) : checklist;
    const steps = cat === 'involuntary' ? INVOLUNTARY_CHECKLIST : VOLUNTARY_CHECKLIST;
    const done = Object.values(cl).filter(v => v.completed).length;
    return Math.round((done / steps.length) * 100);
  };

  if (!['super_admin','hr_officer'].includes(user?.role)) return (
    <div className="empty-state"><h3>Access Restricted</h3></div>
  );

  const filtered = activeTab === 'all' ? offboardings :
    offboardings.filter(o => (o.category||'voluntary') === activeTab);

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">👤 Employee Offboarding</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Manage staff exit process while preserving all records
          </p>
        </div>
        <button className="btn btn-primary" onClick={()=>setShowForm(true)}>
          + Initiate Offboarding
        </button>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:8,marginBottom:16}}>
        {[['all','All'],['voluntary','Voluntary'],['involuntary','Involuntary']].map(([key,label])=>(
          <button key={key} 
            className={'btn btn-sm '+(activeTab===key?'btn-primary':'btn-ghost')}
            onClick={()=>setActiveTab(key)}>
            {label} ({key==='all'?offboardings.length:offboardings.filter(o=>(o.category||'voluntary')===key).length})
          </button>
        ))}
      </div>

      {loading ? <div className="spinner"/> : filtered.length === 0 ? (
        <div className="empty-state">
          <h3>No offboarding records</h3>
          <p>Click + Initiate Offboarding when a staff member is leaving</p>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {filtered.map(ob => {
            const isVoluntary = (ob.category||'voluntary') === 'voluntary';
            const checklist = VOLUNTARY_CHECKLIST;
            const progress = getChecklistProgress(ob.checklist, ob.category);
            const isComplete = ob.completed_at;
            return (
              <div key={ob.id} style={{background:'#fff',border:'1px solid var(--border-light)',
                borderRadius:10,padding:16,cursor:'pointer',
                borderLeft:`4px solid ${isVoluntary?'#556B2F':'#8F2E24'}`}}
                onClick={()=>setSelected(ob)}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                      <span style={{fontWeight:600,fontSize:14,color:'#3B2A21'}}>{ob.employee_name}</span>
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                        background:isVoluntary?'#E8F5EB':'#FDEAEA',
                        color:isVoluntary?'#2E6B3A':'#8F2E24',textTransform:'uppercase'}}>
                        {isVoluntary?'Voluntary':'Involuntary'}
                      </span>
                      <span style={{fontSize:9,fontWeight:600,padding:'2px 8px',borderRadius:20,
                        background:'#F2EFE7',color:'#556B2F'}}>{ob.exit_type}</span>
                      {isComplete && <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',
                        borderRadius:20,background:'#E8F5EB',color:'#2E6B3A'}}>✓ Complete</span>}
                    </div>
                    <div style={{fontSize:12,color:'#8A8A8A'}}>
                      Effective: {ob.effective_date ? new Date(ob.effective_date).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric'}) : '—'}
                      {ob.final_working_date && ` · Last Day: ${new Date(ob.final_working_date).toLocaleDateString('en-NG',{day:'numeric',month:'short'})}`}
                      {ob.notice_period_days && ` · Notice: ${ob.notice_period_days} days`}
                    </div>
                  </div>
                  <div style={{textAlign:'right',minWidth:80}}>
                    <div style={{fontSize:14,fontWeight:700,color:progress===100?'#2E6B3A':'#556B2F'}}>{progress}%</div>
                    <div style={{width:80,height:6,background:'#EDE7D8',borderRadius:3,marginTop:4}}>
                      <div style={{width:progress+'%',height:6,
                        background:progress===100?'#2E6B3A':isVoluntary?'#556B2F':'#8F2E24',borderRadius:3}}/>
                    </div>
                    <div style={{fontSize:9,color:'#8A8A8A',marginTop:2}}>checklist</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:620,width:'92vw'}}>
            <div className="modal-hdr">
              <span className="modal-title">
                {(selected.category||'voluntary')==='voluntary'?'Voluntary':'Involuntary'} Exit — {selected.employee_name}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setSelected(null)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'75vh',overflowY:'auto'}}>
              {/* Info */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:20}}>
                {[
                  ['Exit Type', selected.exit_type],
                  ['Effective Date', selected.effective_date ? new Date(selected.effective_date).toLocaleDateString('en-NG') : '—'],
                  ['Last Working Day', selected.final_working_date ? new Date(selected.final_working_date).toLocaleDateString('en-NG') : '—'],
                  selected.notice_period_days ? ['Notice Period', selected.notice_period_days+' days'] : null,
                  selected.notice_given_date ? ['Notice Given', new Date(selected.notice_given_date).toLocaleDateString('en-NG')] : null,
                  selected.disciplinary_ref ? ['Disciplinary Ref', selected.disciplinary_ref] : null,
                ].filter(Boolean).map(([label,val])=>(
                  <div key={label} style={{background:'#F9F8F5',borderRadius:8,padding:10}}>
                    <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>{label}</div>
                    <div style={{fontSize:12,fontWeight:500,color:'#2C1F16'}}>{val}</div>
                  </div>
                ))}
              </div>

              {selected.termination_reason && (
                <div style={{background:'#FDEAEA',borderRadius:8,padding:12,marginBottom:16,fontSize:12,color:'#8F2E24'}}>
                  <strong>Termination Reason:</strong> {selected.termination_reason}
                </div>
              )}

              {selected.notes && (
                <div style={{background:'#F9F8F5',borderRadius:8,padding:12,marginBottom:16,fontSize:12,color:'#555'}}>
                  <strong>Notes:</strong> {selected.notes}
                </div>
              )}

              {/* Checklist */}
              <div style={{fontSize:11,fontWeight:700,color:'#556B2F',textTransform:'uppercase',
                letterSpacing:'.08em',marginBottom:12}}>Offboarding Checklist</div>
              {(() => {
                const steps = (selected.category||'voluntary')==='involuntary' ? INVOLUNTARY_CHECKLIST : VOLUNTARY_CHECKLIST;
                const cl = selected.checklist ? (typeof selected.checklist==='string' ? JSON.parse(selected.checklist) : selected.checklist) : {};
                return steps.map(step => {
                  const done = cl[step.key]?.completed;
                  return (
                    <div key={step.key} style={{display:'flex',alignItems:'center',gap:10,
                      padding:'10px 12px',borderRadius:8,marginBottom:6,
                      background:done?'#E8F5EB':'#F9F8F5',
                      border:`1px solid ${done?'#B8DEC0':'#E8E0D4'}`}}>
                      <input type="checkbox" checked={!!done}
                        onChange={e=>updateChecklist(selected.id, step.key, e.target.checked)}
                        style={{accentColor:'#556B2F',width:16,height:16,flexShrink:0}} />
                      <span style={{fontSize:13,color:done?'#2E6B3A':'#3B2A21',
                        textDecoration:done?'line-through':'none'}}>{step.label}</span>
                      {done && <span style={{fontSize:10,color:'#2E6B3A',marginLeft:'auto'}}>✓</span>}
                    </div>
                  );
                });
              })()}

              {!selected.completed_at && (
                <button className="btn btn-danger" style={{width:'100%',marginTop:16}}
                  onClick={()=>completeOffboarding(selected.id, selected.employee_name)}>
                  Complete Offboarding & Disable Account
                </button>
              )}
              {selected.completed_at && (
                <div style={{background:'#E8F5EB',borderRadius:8,padding:12,
                  marginTop:16,fontSize:12,color:'#2E6B3A',textAlign:'center'}}>
                  ✓ Offboarding completed on {new Date(selected.completed_at).toLocaleDateString('en-NG')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Offboarding Form */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:560,width:'92vw'}}>
            <div className="modal-hdr">
              <span className="modal-title">Initiate Offboarding</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'80vh',overflowY:'auto'}}>

              {/* Category toggle */}
              <div style={{display:'flex',gap:0,marginBottom:20,
                border:'1.5px solid #E8E0D4',borderRadius:10,overflow:'hidden'}}>
                <button onClick={()=>{setCategory('voluntary');setForm({...form,exit_type:'Resignation'});}}
                  style={{flex:1,padding:'10px',border:'none',cursor:'pointer',fontWeight:600,fontSize:13,
                    background:category==='voluntary'?'#556B2F':'#fff',
                    color:category==='voluntary'?'#fff':'#8A8A8A'}}>
                  ✋ Voluntary Exit
                </button>
                <button onClick={()=>{setCategory('involuntary');setForm({...form,exit_type:'Termination'});}}
                  style={{flex:1,padding:'10px',border:'none',cursor:'pointer',fontWeight:600,fontSize:13,
                    background:category==='involuntary'?'#8F2E24':'#fff',
                    color:category==='involuntary'?'#fff':'#8A8A8A'}}>
                  ⚡ Involuntary Exit
                </button>
              </div>

              <div className="form-group">
                <label className="form-label">Employee *</label>
                <select className="form-control" value={form.user_id}
                  onChange={e=>setForm({...form,user_id:e.target.value})}>
                  <option value="">Select employee</option>
                  {users.map(u=>(<option key={u.id} value={u.id}>{u.name}</option>))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Exit Type *</label>
                  <select className="form-control" value={form.exit_type}
                    onChange={e=>setForm({...form,exit_type:e.target.value})}>
                    {(category==='voluntary'?VOLUNTARY_TYPES:INVOLUNTARY_TYPES).map(t=>(
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Effective Date *</label>
                  <input type="date" className="form-control" value={form.effective_date}
                    onChange={e=>setForm({...form,effective_date:e.target.value})} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Last Working Day</label>
                  <input type="date" className="form-control" value={form.final_working_date}
                    onChange={e=>setForm({...form,final_working_date:e.target.value})} />
                </div>
                {category==='voluntary' && (
                  <div className="form-group">
                    <label className="form-label">Notice Period (days)</label>
                    <input type="number" className="form-control" value={form.notice_period_days}
                      onChange={e=>setForm({...form,notice_period_days:e.target.value})} />
                  </div>
                )}
              </div>

              {category==='voluntary' && (
                <div className="form-group">
                  <label className="form-label">Notice Given Date</label>
                  <input type="date" className="form-control" value={form.notice_given_date}
                    onChange={e=>setForm({...form,notice_given_date:e.target.value})} />
                </div>
              )}

              {category==='involuntary' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Termination Reason *</label>
                    <textarea className="form-control" rows={3} value={form.termination_reason}
                      onChange={e=>setForm({...form,termination_reason:e.target.value})}
                      placeholder="State the reason for termination clearly..." />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Disciplinary Reference</label>
                    <input className="form-control" value={form.disciplinary_ref}
                      onChange={e=>setForm({...form,disciplinary_ref:e.target.value})}
                      placeholder="e.g. Warning letter ref, query ref..." />
                  </div>
                </>
              )}

              <div className="form-group">
                <label className="form-label">Tasks Reassigned To</label>
                <select className="form-control" value={form.tasks_reassigned_to}
                  onChange={e=>setForm({...form,tasks_reassigned_to:e.target.value})}>
                  <option value="">Select staff member</option>
                  {users.filter(u=>u.id!==form.user_id).map(u=>(
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes}
                  onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>

              {category==='involuntary' && (
                <div style={{background:'#FDEAEA',borderRadius:8,padding:12,
                  fontSize:12,color:'#8F2E24',marginBottom:8}}>
                  ⚠ Involuntary exits require completed disciplinary process documentation before initiating.
                </div>
              )}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className={saving?'btn btn-primary':'btn '+(category==='voluntary'?'btn-primary':'btn-danger')}
                onClick={initiate} disabled={saving}>
                {saving?'Initiating...':category==='voluntary'?'Initiate Voluntary Exit':'Initiate Termination'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
